import { useState } from "react";
import { FormattedMessage } from "react-intl";

// Components
import {
    Box,
    Button,
    Typography,
    IconButton,
    Popper,
    Switch,
    Alert,
    AlertTitle,
    ClickAwayListener,
    Paper,
    Skeleton,
} from "@mui/material";
import { TagName } from "components/TagName";

// Services
import { TuserData, updateUserOptinsService } from "services";

// Store
import { setToastConfig } from "store/slice/UI";

// Utils
import { useAppziWidget } from "utils/hooks";

// icons
import InfoIcon from "@mui/icons-material/Info";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

// styles
import privacySettingsStyles from "./PrivacySettings.styles";

// images
import privacyEqualizer from "assets/images/pngs/privacy-equalizer.png";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { getUserOrgs } from "store/slice/sysadmin/userOrgs/userOrgsSlice";

// types
import { Hub } from "../AccountManager";

const PrivacySettings = ({
    userData,
    dataPrivacyChecked,
    setDataPrivacyChecked,
    hubAccess,
    setHubAccess,
    isLoading,
}: {
    userData: TuserData;
    dataPrivacyChecked: boolean;
    setDataPrivacyChecked: React.Dispatch<React.SetStateAction<boolean>>;
    hubAccess: Hub[];
    setHubAccess: React.Dispatch<React.SetStateAction<Hub[]>>;
    isLoading: boolean;
}) => {
    const classes = privacySettingsStyles();

    const { handleOpenWidget } = useAppziWidget();
    const dispatch = useAppDispatch();

    const userOrganizations = useAppSelector(getUserOrgs);

    const [showWarning, setShowWarning] = useState(false);
    const [anchorEl, setAnchorEl] = useState<{ element: null | HTMLElement; id: string }>({ element: null, id: "" });
    const shareWithAll = hubAccess?.every((hub) => hub.sharing);

    const open = Boolean(anchorEl.element);

    const handleUpdateDataPrivacy = async (newVal: boolean) => {
        try {
            const { optin_marketing_email, optin_privacy_policy } = userData;

            await updateUserOptinsService({
                privacy_policy: optin_privacy_policy ?? false,
                marketing_email: optin_marketing_email ?? false,
                share_data: newVal,
                org_share_data: hubAccess.map((hub) => ({
                    org_id: hub.id,
                    optin_share_data: hub.sharing,
                })),
            });
            setDataPrivacyChecked(newVal);
        } catch (error) {
            setDataPrivacyChecked(!newVal);
            console.error(error);
        } finally {
            setShowWarning(false);
        }
    };

    const handleClick = (event: React.MouseEvent<HTMLElement>, id: string) => {
        setAnchorEl({ element: event.currentTarget, id });
    };

    const handleSwitchChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.checked) {
            setShowWarning(true);
        } else {
            handleUpdateDataPrivacy(event.target.checked);
        }
    };

    const handleHubSwitchChange = async (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const newVal = event.target.checked;
        const updatedHubAccess = hubAccess.map((hub, i) => (i === index ? { ...hub, sharing: newVal } : hub));

        setHubAccess(updatedHubAccess);

        const payloadHubShareData = updatedHubAccess.map((hub) => ({
            org_id: hub.id,
            optin_share_data: hub.sharing,
        }));

        try {
            const { optin_marketing_email, optin_privacy_policy } = userData;

            await updateUserOptinsService({
                privacy_policy: optin_privacy_policy ?? false,
                marketing_email: optin_marketing_email ?? false,
                share_data: dataPrivacyChecked,
                org_share_data: payloadHubShareData,
            });

            if (!updatedHubAccess[index].sharing) {
                dispatch(
                    setToastConfig({
                        title: "Privacy settings changed.",
                        content: `You are no longer sharing your PACE profile with ${updatedHubAccess[index].name}.`,
                    })
                );
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleGlobalSwitchChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const newSharingState = event.target.checked;

        const updatedHubAccess = hubAccess.map((hub) => ({
            ...hub,
            sharing: newSharingState,
        }));

        setHubAccess(updatedHubAccess);

        const payloadHubShareData = updatedHubAccess.map((hub) => ({
            org_id: hub.id,
            optin_share_data: newSharingState,
        }));

        try {
            const { optin_marketing_email, optin_privacy_policy } = userData;
            await updateUserOptinsService({
                privacy_policy: optin_privacy_policy ?? false,
                marketing_email: optin_marketing_email ?? false,
                share_data: dataPrivacyChecked,
                org_share_data: payloadHubShareData,
            });
        } catch (error) {
            console.error(error);
        }
    };

    return (
        <Box>
            {/* Warning */}
            {!isLoading && Boolean(!dataPrivacyChecked && !showWarning) && (
                <Box className={classes.textDisabledDataPrivacy}>
                    <WarningAmberRoundedIcon className="icon" />
                    <Typography variant="subtitle2">
                        <FormattedMessage id="accountManager.privacySettings.dataPrivacy.alert" />
                    </Typography>
                </Box>
            )}

            <Box className={classes.mainPaper} sx={{ height: `calc(100vh - 296px)` }}>
                <Box sx={{ flex: !isLoading && !userOrganizations.data.length ? 0 : 1 }}>
                    {isLoading ? (
                        <>
                            <Box>
                                <Skeleton animation="wave" sx={{ fontSize: 42 }} />
                                <Skeleton variant="rectangular" animation="wave" height={64} />
                            </Box>
                            <Box>
                                <Skeleton animation="wave" sx={{ fontSize: 42 }} />
                                <Skeleton variant="rectangular" animation="wave" height={64} />
                            </Box>
                        </>
                    ) : (
                        <>
                            {/* Hub Access Section */}
                            {userOrganizations.data.length > 0 && (
                                <Box>
                                    <Box display="flex">
                                        <Typography variant="h6" className={classes.rowTitle}>
                                            <FormattedMessage id="accountManager.privacySettings.hubAccessTitle" />
                                        </Typography>
                                        <IconButton
                                            onClick={(ev) => handleClick(ev, "hubAccess")}
                                            disableRipple={true}
                                            disableFocusRipple={true}
                                        >
                                            <InfoIcon className={classes.infoIcon} />
                                        </IconButton>
                                        <ClickAwayListener
                                            mouseEvent="onMouseDown"
                                            touchEvent="onTouchStart"
                                            onClickAway={() => setAnchorEl({ element: null, id: "" })}
                                        >
                                            <Popper
                                                id="hub-popper"
                                                open={open && anchorEl.id === "hubAccess"}
                                                anchorEl={anchorEl.element}
                                                placement="bottom-start"
                                            >
                                                <Paper
                                                    className={classes.paper}
                                                    sx={{ width: "569px !important" }}
                                                    elevation={1}
                                                >
                                                    <Box display="flex" alignItems="center" mb={1}>
                                                        <InfoIcon
                                                            className={classes.infoIcon}
                                                            sx={{ fontSize: "20px !important" }}
                                                        />
                                                        <Typography variant="h6" className={classes.tooltipTitle}>
                                                            <FormattedMessage id="accountManager.privacySettings.hubAccessTooltip.title" />
                                                        </Typography>
                                                    </Box>
                                                    <Typography sx={{ lineHeight: "24px", marginBottom: 2 }}>
                                                        <FormattedMessage id="accountManager.privacySettings.hubAccessTooltip.body" />
                                                    </Typography>
                                                </Paper>
                                            </Popper>
                                        </ClickAwayListener>
                                    </Box>

                                    <Box>
                                        <Typography variant="body1" className={classes.hubAccessDescription}>
                                            <FormattedMessage id="accountManager.privacySettings.hubAccessText" />
                                        </Typography>
                                    </Box>
                                    {userOrganizations.data.length > 1 ? (
                                        <Box sx={{ display: "flex", mb: 2 }}>
                                            <Switch
                                                disabled={!dataPrivacyChecked}
                                                size="small"
                                                checked={shareWithAll}
                                                onChange={handleGlobalSwitchChange}
                                                className={classes.switch}
                                            />
                                            <Typography variant="body1" sx={{ color: "text.secondary" }}>
                                                Sharing with All Hubs
                                            </Typography>
                                        </Box>
                                    ) : null}

                                    <Box>
                                        {userOrganizations.data.map((hub, index) => (
                                            <Box key={hub.id} className={classes.hubContainer}>
                                                <TagName>{hub.name}</TagName>
                                                <Box display="flex" ml={1}>
                                                    <Typography variant="body1" sx={{ color: "text.secondary" }}>
                                                        Not Sharing
                                                    </Typography>
                                                    <Switch
                                                        size="small"
                                                        className={classes.switch}
                                                        disabled={!dataPrivacyChecked}
                                                        checked={hubAccess[index]?.sharing}
                                                        onChange={(event) => handleHubSwitchChange(event, index)}
                                                    />
                                                    <Typography variant="body1" sx={{ color: "text.secondary" }}>
                                                        Sharing
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            )}
                        </>
                    )}
                </Box>

                {!isLoading && (
                    <Box width="85%">
                        {/* Data Privacy Section */}
                        <Box>
                            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                                <Typography variant="h6" className={classes.rowTitle}>
                                    <FormattedMessage id="accountManager.privacySettings.dataPrivacyTitle" />
                                </Typography>

                                <IconButton
                                    onClick={(ev) => handleClick(ev, "dataPrivacy")}
                                    disableRipple={true}
                                    disableFocusRipple={true}
                                >
                                    <InfoIcon className={classes.infoIcon} />
                                </IconButton>
                                <ClickAwayListener
                                    mouseEvent="onMouseDown"
                                    touchEvent="onTouchStart"
                                    onClickAway={() => setAnchorEl({ element: null, id: "" })}
                                >
                                    <Popper
                                        id="data-privacy-popper"
                                        open={open && anchorEl.id === "dataPrivacy"}
                                        anchorEl={anchorEl.element}
                                        placement="bottom-start"
                                    >
                                        <Paper className={classes.paper} elevation={1}>
                                            <Typography>
                                                <FormattedMessage id="accountManager.privacySettings.popper.text1" />
                                            </Typography>
                                            <img src={privacyEqualizer} alt="privacyEqualizer" />
                                            <Typography sx={{ mb: 2 }}>
                                                <FormattedMessage id="accountManager.privacySettings.popper.text2" />
                                            </Typography>
                                            <Typography>
                                                <FormattedMessage id="accountManager.privacySettings.popper.text3" />
                                            </Typography>
                                        </Paper>
                                    </Popper>
                                </ClickAwayListener>
                            </Box>

                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <Typography variant="body1">
                                    <FormattedMessage id="accountManager.privacySettings.dataPrivacyText" />
                                </Typography>
                                <Switch
                                    size="small"
                                    sx={{ marginLeft: "16px" }}
                                    checked={dataPrivacyChecked}
                                    onChange={(event) => handleSwitchChange(event)}
                                    className={classes.switch}
                                />
                            </Box>
                            {showWarning && (
                                <Alert
                                    severity="error"
                                    variant="filled"
                                    icon={false}
                                    className={classes.alert}
                                    sx={{ padding: 1 }}
                                >
                                    <AlertTitle>
                                        <Box sx={{ display: "flex", alignItems: "center", marginBottom: 2 }}>
                                            <ErrorOutlineIcon sx={{ fontSize: 18, marginRight: 1 }} />
                                            <Typography variant="subtitle1" sx={{ lineHeight: "24px" }}>
                                                <FormattedMessage id="accountManager.privacySettings.dataPrivacy.switchAlert" />
                                            </Typography>
                                        </Box>
                                    </AlertTitle>

                                    <Box className={classes.warningMessageButtonsContainer}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            className="cancel"
                                            onClick={() => setShowWarning(false)}
                                        >
                                            <FormattedMessage id="accountManager.privacySettings.cancel" />
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            className="accept"
                                            onClick={() => handleUpdateDataPrivacy(false)}
                                        >
                                            <FormattedMessage id="accountManager.privacySettings.yesIAmSure" />
                                        </Button>
                                    </Box>
                                </Alert>
                            )}
                        </Box>
                        <Box display="flex" alignItems="center">
                            <Typography variant="subtitle2">
                                <FormattedMessage id="accountManager.privacySettings.footer" />
                            </Typography>
                            <Button
                                variant="text"
                                size="small"
                                className={classes.footerButton}
                                onClick={handleOpenWidget}
                            >
                                <FormattedMessage id="accountManager.privacySettings.footerButton" />
                            </Button>
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default PrivacySettings;
